// Meeting email (spec §3.5, §4.4): a pure function from a meeting to a self-contained HTML email.
// It ALWAYS renders through publicView/publicLoeRows itself, so a caller can't email internal
// data by passing the raw meeting. Table layout + inline styles only (email clients ignore <style>),
// 680px wide, Georgia/Arial. Every string is escaped; only http(s), non-SharePoint links are links.
import { publicView, publicLoeRows, isSharePointUrl, stripSharePointLinks } from "./publicView.js";
import { parseBody } from "./markdownLite.js";
import { paper } from "./docTheme.js";
import { visibleCrm } from "./crmBlock.js";
import { displayName } from "../utils/theme.js";

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Arial, Helvetica, sans-serif";
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
// A bad/missing date string must never surface "Invalid Date" in rendered output or subjects.
const parseDate = (d) => {
  if (!d) return null;
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};
const longDate = (d) => {
  const dt = parseDate(d);
  return dt ? dt.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "";
};
const shortDate = (d) => {
  const dt = parseDate(d);
  return dt ? dt.toLocaleDateString("en-US", { day: "numeric", month: "short" }) : "";
};
const shortDateWithYear = (d) => {
  const dt = parseDate(d);
  return dt ? dt.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "";
};
const year = (d) => (d ? String(d).slice(0, 4) : "");

export function defaultSubject(meeting, clientName = "") {
  const m = meeting || {};
  if (m.type === "kickoff") return `${clientName} × Sawtooth — Kickoff meeting summary (${longDate(m.meetingDate)})`;
  const startYear = year(m.periodStart);
  const endYear = year(m.periodEnd);
  const range = startYear && endYear && startYear !== endYear
    ? `${shortDateWithYear(m.periodStart)} – ${shortDateWithYear(m.periodEnd)}`
    : `${shortDate(m.periodStart)} – ${shortDate(m.periodEnd)}, ${endYear}`;
  return `${clientName} × Sawtooth — Update Meeting ${m.number} summary (${range})`;
}

function spansHtml(spans) {
  return spans.map((s) => {
    if (s.t === "b") return `<strong>${esc(s.v)}</strong>`;
    if (s.t === "a" && /^https?:\/\//i.test(s.href) && !isSharePointUrl(s.href)) {
      return `<a href="${esc(s.href)}" style="color:${paper.accent};text-decoration:underline;">${esc(s.v)}</a>`;
    }
    return esc(s.v);
  }).join("");
}

function bodyHtml(text) {
  return parseBody(text).map((b) => (b.type === "ul"
    ? `<ul style="margin:0 0 10px;padding-left:22px;">${b.items.map((sp) => `<li style="margin:0 0 4px;">${spansHtml(sp)}</li>`).join("")}</ul>`
    : `<p style="margin:0 0 10px;">${b.lines.map(spansHtml).join("<br>")}</p>`)).join("");
}

function introHtml(intro) {
  const text = stripSharePointLinks(String(intro || "").trim());
  if (!text) return "";
  const paras = text.split(/\n\s*\n/).map((p) => `<p style="margin:0 0 12px;">${esc(p).replace(/\r?\n/g, "<br>")}</p>`).join("");
  return `<tr><td style="padding:0 0 24px;font-family:${SANS};font-size:15px;line-height:1.55;color:${paper.ink};">${paras}</td></tr>`;
}

const ownerText = (o, users) => {
  if (!o) return null;
  if (o.type === "sawtooth") return "Sawtooth";
  if (o.type === "client") return "Client";
  const u = (users || []).find((x) => x.id === o.userId);
  return u ? displayName(u) : "Sawtooth";
};

const h2 = (t) => `<tr><td style="padding:28px 0 12px;font-family:${SERIF};font-size:24px;font-weight:bold;color:${paper.ink};">${esc(t)}</td></tr>`;

function tilesHtml(tiles) {
  if (!tiles.length) return "";
  const w = Math.floor(100 / tiles.length);
  const cells = tiles.map((t) => {
    const bg = t.warn ? paper.warnBg : paper.card;
    return `<td width="${w}%" valign="top" style="padding:4px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" bgcolor="${bg}" style="background:${bg};border:1px solid ${t.warn ? paper.warnLine : paper.line};"><tr><td style="padding:14px;font-family:${SERIF};font-size:28px;font-weight:bold;color:${t.warn ? paper.warnInk : paper.ink};">${esc(t.value)}</td></tr><tr><td style="padding:0 14px 14px;font-family:${SANS};font-size:12px;color:${t.warn ? paper.warnInk : paper.muted};">${esc(t.label)}</td></tr></table></td>`;
  }).join("");
  return `<tr><td style="padding:20px 0 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>${cells}</tr></table></td></tr>`;
}

function loeHtml(rows) {
  if (!rows.length) return "";
  const cards = rows.map((r) => {
    const pct = Math.max(0, Math.min(100, Math.round(Number(r.pct) || 0)));
    const done = pct === 100;
    const color = done ? paper.success : paper.accent;
    const bar = pct > 0
      ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td width="${pct}%" height="8" bgcolor="${color}" style="background:${color};font-size:0;line-height:0;">&nbsp;</td>${pct < 100 ? `<td height="8" bgcolor="${paper.lineSoft}" style="background:${paper.lineSoft};font-size:0;line-height:0;">&nbsp;</td>` : ""}</tr></table>`
      : `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td height="8" bgcolor="${paper.lineSoft}" style="background:${paper.lineSoft};font-size:0;line-height:0;">&nbsp;</td></tr></table>`;
    const doneLine = r.doneInPeriod.length ? `<div style="margin-top:8px;"><strong style="color:${paper.success};">Done this period:</strong> ${esc(r.doneInPeriod.join(" · "))}</div>` : "";
    const nextLine = r.upNext.length ? `<div style="margin-top:4px;"><strong style="color:${paper.accent};">Up next:</strong> ${esc(r.upNext.map((u) => (u.dueDate ? `${u.name} (${u.dueDate})` : u.name)).join(" · "))}</div>` : "";
    return `<tr><td style="padding:0 0 12px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${paper.card};border:1px solid ${paper.line};"><tr><td style="padding:16px 20px;font-family:${SANS};font-size:14px;color:${paper.ink};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="font-size:16px;font-weight:bold;">${esc(r.name)}</td><td align="right" style="font-size:13px;color:${paper.muted};white-space:nowrap;">${esc(`${r.done} of ${r.total} tasks`)} · <strong style="color:${color};">${done ? "Complete" : `${pct}%`}</strong></td></tr></table>
<div style="height:10px;line-height:10px;font-size:0;">&nbsp;</div>${bar}${doneLine}${nextLine}</td></tr></table></td></tr>`;
  }).join("");
  return h2("Lines of effort") + cards;
}

function crmHtml(block) {
  if (!block) return "";
  const c = block.counts;
  const tiles = tilesHtml([
    { key: "contacts", value: c.contacts, label: "contacts" }, { key: "lead", value: c.lead, label: "leads" },
    { key: "inProgress", value: c.inProgress, label: "in progress" }, { key: "won", value: c.won, label: "won" },
  ]);
  const rows = block.events.map((e) => `<tr><td style="padding:10px 0;border-top:1px solid ${paper.line};font-family:${SANS};font-size:15px;line-height:1.5;color:${paper.ink};">
<div style="font-weight:bold;">${esc(e.headline)}</div>${e.detail ? `<div style="font-size:13px;color:${paper.muted};">${esc(e.detail)}${e.date ? ` · ${esc(e.date)}` : ""}</div>` : ""}${e.text ? `<div style="margin-top:4px;white-space:pre-wrap;">${esc(e.text)}</div>` : ""}</td></tr>`).join("");
  const list = rows ? `<tr><td style="padding:16px 0 0;font-family:${SANS};font-size:13px;font-weight:bold;letter-spacing:1px;text-transform:uppercase;color:${paper.muted};">What’s new</td></tr>${rows}` : "";
  return h2("Contacts & outreach") + tiles + list;
}

function agendaHtml(section) {
  const rows = section.items.map((i) => `<tr><td width="22" valign="top" style="font-family:${SANS};font-size:15px;color:${i.checked ? paper.success : paper.muted};">${i.checked ? "✓" : "○"}</td>
<td style="padding:0 0 6px;font-family:${SANS};font-size:15px;color:${paper.ink};">${esc(i.headline)}</td></tr>`).join("");
  return h2("Agenda covered") + `<tr><td><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows}</table></td></tr>`;
}

function sectionHtml(section, users) {
  const cards = section.items.map((i) => {
    const owner = ownerText(i.owner, users);
    const meta = [owner, i.dueDate].filter(Boolean).join(" · ");
    const badge = i.decisionNeeded ? `<div style="margin:0 0 6px;"><span style="font-size:11px;font-weight:bold;letter-spacing:1px;text-transform:uppercase;color:${paper.warnInk};background:#fce0a8;padding:2px 6px;">Decision needed</span></div>` : "";
    const bg = i.decisionNeeded ? paper.warnBg : paper.card;
    return `<tr><td style="padding:0 0 12px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" bgcolor="${bg}" style="background:${bg};border:1px solid ${i.decisionNeeded ? paper.warnLine : paper.line};"><tr><td style="padding:16px 20px;font-family:${SANS};font-size:15px;line-height:1.55;color:${paper.ink};">
${badge}<div style="font-size:17px;font-weight:bold;margin:0 0 4px;">${esc(i.headline)}</div>${meta ? `<div style="font-size:13px;font-weight:bold;color:${i.owner?.type === "client" ? paper.warnInk : paper.muted};margin:0 0 8px;">${esc(meta)}</div>` : ""}${bodyHtml(i.body)}</td></tr></table></td></tr>`;
  }).join("");
  return h2(section.title) + cards;
}

export function renderMeetingEmail(meeting, { clientName = "", intro = "", loeRows = [], tiles = [], users = [], crm = null } = {}) {
  const view = publicView(meeting);
  const hiddenObjectiveIds = new Set(view.loe.hiddenObjectiveIds);
  const rows = publicLoeRows(loeRows).filter((r) => !hiddenObjectiveIds.has(r.id));
  const hiddenTileKeys = new Set(view.summary.hiddenTiles);
  const visibleTiles = (tiles || []).filter((t) => !hiddenTileKeys.has(t.key));
  const kickoff = view.type === "kickoff";
  const typeLabel = kickoff ? "Kickoff meeting" : `Bi-weekly update · Meeting ${view.number}`;
  const when = kickoff ? longDate(view.meetingDate) : `${longDate(view.periodStart)} – ${longDate(view.periodEnd)}`;
  const sections = view.sections.filter((s) => s.items.length > 0)
    .map((s) => (s.kind === "agenda" ? agendaHtml(s) : sectionHtml(s, users))).join("");
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${esc(typeLabel)}</title></head>
<body style="margin:0;padding:0;background:${paper.bg};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${paper.bg};"><tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="680" cellpadding="0" cellspacing="0" style="width:680px;max-width:100%;">
${introHtml(intro)}
<tr><td style="padding:0 0 4px;font-family:${SANS};font-size:12px;font-weight:bold;letter-spacing:1.4px;text-transform:uppercase;color:${paper.accent};">${esc(typeLabel)}</td></tr>
<tr><td style="padding:0 0 12px;font-family:${SANS};font-size:14px;color:${paper.muted};">${esc(`${clientName} & Sawtooth Advisors · ${when}`)}</td></tr>
${view.summary.headline ? `<tr><td style="padding:0 0 8px;font-family:${SERIF};font-size:34px;line-height:1.15;color:${paper.ink};">${esc(view.summary.headline)}</td></tr>` : ""}
${kickoff ? "" : tilesHtml(visibleTiles)}
${loeHtml(rows)}${crmHtml(visibleCrm(crm, view))}
${sections}
<tr><td style="padding:28px 0 0;border-top:1px solid ${paper.line};font-family:${SANS};font-size:12px;color:${paper.muted};">Sent by Sawtooth Advisors. Reply to this email to reach your project team.</td></tr>
</table></td></tr></table></body></html>`;
}
